import React from 'react';

import { datadogRum } from '@datadog/browser-rum';

import { getItemFromStorage, setItemInStorage } from 'src/utils/storageUtils';

/**
 * This utility function is meant to be used with React.lazy() to retry
 * fetching a component by refreshing the page when it encounters an error.
 */
export function fetchWithRetry(
    componentToImport: () => Promise<{ default: React.ComponentType<any> }>,
    componentName: string | undefined
): Promise<{ default: React.ComponentType<any> }> {
    return new Promise((resolve, reject) => {
        // Key records the timestamp (ms) of our last forced refresh for this chunk.
        const storageKey = !!componentName
            ? `has-retried-refresh-${componentName}`
            : 'has-retried-refresh';

        // If the chunk no longer exists in origin, we will attempt to refresh the page once to get the new chunk.
        // We will not attempt to refresh again for REFRESH_TTL_MS milliseconds.
        const REFRESH_TTL_MS = 60_000;
        const lastRefreshAt = Number(getItemFromStorage(storageKey, 0)) || 0;
        const refreshedRecently = Date.now() - lastRefreshAt < REFRESH_TTL_MS;

        // Try to import the component
        componentToImport()
            .then((component) => {
                setItemInStorage(storageKey, 0); // Clear retry state after a successful load
                resolve(component);
            })
            .catch((error) => {
                if (!refreshedRecently) {
                    try {
                        setItemInStorage(storageKey, Date.now()); // Record this attempt to bound retries

                        // Redirect to the same page with a time-based search param to force a refresh
                        const currentUrl = new URL(window.location.href);
                        currentUrl.searchParams.set('t', Date.now().toString());
                        return window.location.assign(currentUrl.toString());
                    } catch (error) {
                        // If unable to set state in storage, redirect to home page, which will force a refresh
                        return window.location.assign(`/?t=${Date.now()}`);
                    }
                }

                datadogRum.addError(error, {
                    message: 'Error fetching component after reloading page',
                });
                reject(error); // Default error behavior
            });
    });
}

export default fetchWithRetry;
